/**
 * Broker Fee Repricing Dialog (Feature 30 / Phase 5)
 *
 * Raises a broker-fee repricing on a live contract, FOR APPROVAL. It applies nothing itself —
 * approval does. Submitting creates a 'Pending' contract_fee_changes row plus a pending_changes
 * row (change_type='fee_repricing'); the fee and its schedule change only when an approver acts.
 *
 * WHY THIS EXISTS
 * ---------------
 * A broker fee is struck ONCE, at submission — the broker earned it on the amount agreed then, so
 * a later capital change must never re-strike it. An approved repricing is the SINGLE sanctioned
 * exception, and it VERSIONS rather than overwrites: V1 keeps every accrual before the effective
 * date exactly as recorded, V2 owns the tail. Nothing already accrued or journal-exported is ever
 * rewritten.
 *
 * ⚠ THE LIFETIME TOTAL AFTER A REPRICING EQUALS NEITHER THE OLD FEE NOR THE NEW ONE.
 * It is V1-accrued-to-date + V2-total. Measured on dev contract 68 (60 000 at 1.5% repriced to
 * 2.0%): 29 180.33 + 41 092.90 = 70 273.23, against 60 000 and 80 000. Anyone reconciling a
 * repriced contract against `capital x latest percent` gets the wrong answer by design — which is
 * why the dialog states the effective date so prominently.
 *
 * MODELLED ON AdhocRateChangeDialog, with one deliberate difference: there is NO preview endpoint
 * for a fee repricing, so showPreview={false} and there is no preview modal. Do not add a preview
 * button that cannot preview.
 */

import React, { useState, useEffect } from 'react';
import { TextField, Grid, InputAdornment, MenuItem, Typography, Alert } from '@mui/material';
import { format } from 'date-fns';
import { apiPost } from '../../utils/apiClient';
import ContractActionDialogBase from './ContractActionDialogBase';
import { formatNumber } from '../../utils/formatters';

// Sentinels reinterpreted in fee terms — the same exclusions the broker capture screen applies.
// 998 Adhoc = "manual dates only", not a cadence the engine can step.
// 999 At Maturity would render as "Once-Off", which is the NO-cadence state, expressed here by
// the explicit "Once-Off (single payment)" option rather than by a frequency row.
const MONTHS_ADHOC = 998;
const MONTHS_AT_MATURITY = 999;

// The value used by the cadence select to mean "no cadence at all". It cannot be '' — that is
// indistinguishable from "not yet chosen" — and it must not be a real frequency id.
const ONCE_OFF = '__once_off__';

const FeeRepricingDialog = ({ open, onClose, contract, contractId, fee, frequencies = [], onSuccess }) => {
  const cid = contractId || contract?.id;
  const ccy = contract?.currency_code || '';

  // The CURRENT fee, read from the junction row. These are the values every field is seeded with.
  const currentPercent = fee?.fee_percent != null ? parseFloat(fee.fee_percent) : null;
  const currentAmount = fee?.broker_fee_exclusive_amount != null
    ? parseFloat(fee.broker_fee_exclusive_amount) : null;
  const currentFrequencyId = fee?.fee_frequency_id ?? null;

  const [formData, setFormData] = useState({
    new_fee_percent: '',
    new_fee_amount: '',
    new_frequency_id: '',
    effective_date: format(new Date(), 'yyyy-MM-dd'),
  });
  const [motivation, setMotivation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // ⚠ EVERY FIELD IS PREFILLED WITH THE CURRENT VALUE — NEVER BLANK (user decision, 2026-09-08).
  // A blank money field reads as zero, or as a value that failed to load, rather than as
  // "unchanged". The user edits only what is actually being renegotiated, and the approver is
  // shown old -> new for all three dimensions regardless.
  useEffect(() => {
    if (!open) return;
    setFormData({
      new_fee_percent: currentPercent != null ? String(currentPercent) : '',
      new_fee_amount: currentAmount != null ? String(currentAmount) : '',
      new_frequency_id: currentFrequencyId != null ? String(currentFrequencyId) : ONCE_OFF,
      effective_date: format(new Date(), 'yyyy-MM-dd'),
    });
    setMotivation('');
    setError(null);
  }, [open, currentPercent, currentAmount, currentFrequencyId]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const cadenceOptions = (frequencies || []).filter(
    (f) => f && f.active !== false
      && f.months_value !== MONTHS_ADHOC
      && f.months_value !== MONTHS_AT_MATURITY
  );

  const pct = parseFloat(formData.new_fee_percent);
  const amt = parseFloat(formData.new_fee_amount);
  const freqRaw = formData.new_frequency_id;
  const clearFrequency = freqRaw === ONCE_OFF;
  const freqId = clearFrequency ? null : (freqRaw === '' ? null : parseInt(freqRaw, 10));

  // Has anything actually moved? The backend refuses a no-op (it would open a phantom V2
  // superseding nothing), so catch it here and say so rather than surfacing a 400 after submit.
  const percentChanged = Number.isFinite(pct) && (currentPercent == null || Math.abs(pct - currentPercent) > 0.00005);
  const amountChanged = Number.isFinite(amt) && (currentAmount == null || Math.abs(amt - currentAmount) > 0.005);
  const frequencyChanged = (freqId ?? null) !== (currentFrequencyId ?? null);
  const somethingChanged = percentChanged || amountChanged || frequencyChanged;

  // The term bounds the SP enforces. Stating them as input limits means the user is told before
  // submitting rather than after an approver has already looked at it.
  const termStart = (contract?.funds_received_date || contract?.deposit_date || '').substring(0, 10);
  const termEnd = (contract?.maturity_date || '').substring(0, 10);
  const dateInTerm = !!formData.effective_date
    && (!termStart || formData.effective_date >= termStart)
    && (!termEnd || formData.effective_date <= termEnd);

  const isValid = !!formData.effective_date && dateInTerm && somethingChanged && !!motivation;

  const handleSubmit = async ({ proof_document_ids }) => {
    if (!isValid) {
      setError('Change at least one of percentage, amount or cadence, and provide a motivation.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await apiPost(`/broker-fees/contract/${cid}/reprice`, {
        // Send each dimension only when it actually moved. The API treats a null/absent dimension
        // as UNCHANGED and carries the current value forward — which is NOT the same as clearing
        // it, hence the separate clear_frequency flag.
        new_fee_percent: percentChanged ? pct : null,
        new_fee_amount: amountChanged ? amt : null,
        new_frequency_id: (frequencyChanged && !clearFrequency) ? freqId : null,
        clear_frequency: frequencyChanged && clearFrequency,
        effective_date: formData.effective_date,
        reason: motivation,
        proof_document_ids,
      });
      if (response?.success) {
        onClose();
        if (onSuccess) onSuccess();
      } else {
        setError(response?.message || 'Failed to submit fee repricing');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit fee repricing');
    } finally {
      setSubmitting(false);
    }
  };

  const currentCadenceLabel = currentFrequencyId == null
    ? 'Once-Off'
    : (cadenceOptions.find((f) => f.id === currentFrequencyId)?.frequency || 'Once-Off');

  return (
    <ContractActionDialogBase
      open={open}
      onClose={onClose}
      contract={contract}
      title="Reprice Broker Fee"
      description={
        'Renegotiate the broker fee on this contract from a chosen effective date. '
        + 'Everything accrued BEFORE that date stays exactly as recorded (version 1); a new version '
        + 'owns the remaining term. The change applies only once an approver has acted.'
      }
      submitLabel="Submit for Approval"
      submitColor="info"
      isValid={isValid}
      submitting={submitting}
      error={error}
      onClearError={() => setError(null)}
      onSubmit={handleSubmit}
      /* No preview: there is no fee-repricing preview endpoint. A Preview button that cannot
         preview is worse than none. */
      showPreview={false}
      workflowCode="fee_repricing"
      reason={motivation}
      onReasonChange={setMotivation}
      reasonLabel="Motivation (Required)"
      reasonPlaceholder="Why is the broker fee being renegotiated? This will be reviewed by an approver."
      reasonHelperText="A motivation is mandatory — a broker fee is a commercial figure and an approver must see why it moved."
    >
      <Grid container spacing={2}>
        <Grid size={6}>
          <TextField
            label="New Fee %"
            type="number"
            value={formData.new_fee_percent}
            onChange={(e) => handleFieldChange('new_fee_percent', e.target.value)}
            fullWidth
            inputProps={{ step: '0.0001', min: 0 }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            helperText={currentPercent != null ? `Current: ${currentPercent}%` : ' '}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            label="New Fee Amount (Excl VAT)"
            type="number"
            value={formData.new_fee_amount}
            onChange={(e) => handleFieldChange('new_fee_amount', e.target.value)}
            fullWidth
            inputProps={{ step: '0.01', min: 0 }}
            InputProps={{ startAdornment: ccy ? <InputAdornment position="start">{ccy}</InputAdornment> : null }}
            helperText={currentAmount != null ? `Current: ${ccy} ${formatNumber(currentAmount)}` : ' '}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            select
            label="Payment Cadence"
            value={formData.new_frequency_id}
            onChange={(e) => handleFieldChange('new_frequency_id', e.target.value)}
            fullWidth
            helperText={`Current: ${currentCadenceLabel}`}
          >
            {/* Once-Off is the NO-cadence state — the whole fee settles in a single payment. It is
                an explicit option here (unlike the capture screen, where the instalments toggle
                expresses it) because a repricing must be able to MOVE to it, and "no cadence" has
                to be distinguishable from "cadence unchanged". */}
            <MenuItem value={ONCE_OFF}>Once-Off (single payment)</MenuItem>
            {cadenceOptions.map((f) => (
              <MenuItem key={f.id} value={String(f.id)}>{f.frequency}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={6}>
          <TextField
            label="Effective Date"
            type="date"
            value={formData.effective_date}
            onChange={(e) => handleFieldChange('effective_date', e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: termStart || undefined, max: termEnd || undefined }}
            helperText="Accruals before this date are never rewritten."
          />
        </Grid>

        {/* ⚠ THE AMOUNT WINS OVER THE PERCENTAGE when both are supplied and they disagree. A
            renegotiated fee is often a flat figure that no longer ties to a clean percentage, and
            the backend applies the agreed AMOUNT. Say so — otherwise a user who edits the
            percentage and leaves a stale amount gets a result they did not intend. */}
        {percentChanged && amountChanged && Number.isFinite(pct) && Number.isFinite(amt) && (
          <Grid size={12}>
            <Alert severity="info">
              Both the percentage and the amount were changed. The <strong>amount</strong> is what
              gets applied — {ccy} {formatNumber(amt)} — and the percentage is retained as
              informational.
            </Alert>
          </Grid>
        )}

        {!somethingChanged && (
          <Grid size={12}>
            <Typography variant="body2" color="text.secondary">
              Nothing has changed yet — edit the percentage, the amount or the cadence.
            </Typography>
          </Grid>
        )}

        {!!formData.effective_date && !dateInTerm && (
          <Grid size={12}>
            <Alert severity="warning">
              The effective date must fall inside the contract term
              {termStart && termEnd ? ` (${termStart} to ${termEnd})` : ''}. Nothing before the term
              can be repriced, and a date after maturity would leave no remaining term for the new
              version to own.
            </Alert>
          </Grid>
        )}
      </Grid>
    </ContractActionDialogBase>
  );
};

export default FeeRepricingDialog;
